import { useLocation, Link, useParams } from 'react-router-dom'
import {
  Breadcrumb as BreadcrumbUI,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { getBreadcrumbConfig, generateBreadcrumbPath } from './breadcrumbConfig'
import { useKnowledgeStore } from '@/stores/knowledgeStore'
import { useAgentDetail } from '@/api/hooks/useAgent'
import { useChatStore } from '@/stores/chatStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'

// 面包屑项类型
interface BreadcrumbItem {
  title: string
  path: string
  isCurrent: boolean
}

const Breadcrumb = () => {
  const location = useLocation()
  const pathname = location.pathname
  const { currentKnowledge, currentDataset } = useKnowledgeStore()
  const { currentWorkspace } = useWorkspaceStore()
  const params = useParams()

  // 获取智能体详情（只在智能体相关页面）
  const isAgentPage = pathname.startsWith('/agents/')
  const agentId = isAgentPage
    ? params.id || location.search.match(/[?&]id=([^&]+)/)?.[1]
    : undefined
  const { data: agentDetail } = useAgentDetail(agentId)

  // 获取会话信息（如果是会话页面）
  const { sessions } = useChatStore()
  const sessionId = params.sessionId
  const currentSession = sessions.find((s) => s.sessionId === sessionId)

  // 生成面包屑数据
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const breadcrumbs: BreadcrumbItem[] = []

    // 获取当前路径的面包屑路径数组
    const breadcrumbPaths = generateBreadcrumbPath(pathname)

    // 如果路径数组为空，返回空数组
    if (breadcrumbPaths.length === 0) {
      return breadcrumbs
    }

    // 如果不是根路由，在第一级添加workspace信息
    if (breadcrumbPaths[0] !== '/' && currentWorkspace) {
      breadcrumbs.push({
        title: currentWorkspace.name,
        path: '', // 空路径表示不可点击
        isCurrent: false,
      })
    }

    // 构建面包屑项
    breadcrumbPaths.forEach((path, index) => {
      const config = getBreadcrumbConfig(path)
      if (config) {
        // 处理动态标题
        let title: string
        if (typeof config.title === 'function') {
          title = config.title()
        } else {
          title = config.title
        }

        // 特殊处理知识库数据集页面
        if (path === '/knowledge/dataset' && currentKnowledge?.name) {
          title = currentKnowledge.name
        }

        // 特殊处理数据集详情页面
        if (path === '/knowledge/dataset-detail' && currentDataset?.name) {
          title = currentDataset.name
        }

        // 特殊处理智能体聊天页面
        if (path === '/agents/chat' && agentDetail?.name) {
          title = agentDetail.name
        }

        // 特殊处理会话页面
        if (path === '/agents/chat/session' && currentSession?.title) {
          title = currentSession.title
        }

        // 为智能体聊天页面生成正确的链接路径
        let linkPath = config.path
        if (path === '/agents/chat') {
          // 如果是智能体聊天页面，链接到具体的智能体聊天页面
          if (agentId) {
            linkPath = `/agents/chat/${agentId}`
          }
        }

        breadcrumbs.push({
          title,
          path: linkPath,
          isCurrent: index === breadcrumbPaths.length - 1,
        })
      } else {
        // 如果配置中没有找到，使用路径的最后一段作为标题
        const pathSegments = path.split('/').filter(Boolean)
        const lastSegment = pathSegments[pathSegments.length - 1]
        breadcrumbs.push({
          title: lastSegment || '未知页面',
          path: path,
          isCurrent: index === breadcrumbPaths.length - 1,
        })
      }
    })

    return breadcrumbs
  }

  const breadcrumbs = generateBreadcrumbs()

  // 如果没有面包屑项，不显示组件
  if (breadcrumbs.length === 0) {
    return null
  }

  return (
    <BreadcrumbUI>
      <BreadcrumbList>
        {breadcrumbs.map((item, index) => (
          <div
            key={item.path || `workspace-${index}`}
            className="flex items-center"
          >
            <BreadcrumbItem>
              {item.isCurrent ? (
                <BreadcrumbPage className="text-sm font-medium">
                  {item.title}
                </BreadcrumbPage>
              ) : item.path === '' ? (
                // Workspace项，不可点击，只做展示
                <span className="text-sm text-muted-foreground">
                  {item.title}
                </span>
              ) : (
                <BreadcrumbLink asChild>
                  <Link
                    to={item.path}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {item.title}
                  </Link>
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
            {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
          </div>
        ))}
      </BreadcrumbList>
    </BreadcrumbUI>
  )
}

export default Breadcrumb
